// frontend/smartdocs-admin/src/context/SettingsContext.tsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api  from '@/utils/api';
import { toast } from 'react-hot-toast';
import {useAuth} from "@/components/auth/AuthContext";

interface Settings {
  [category: string]: {
    [key: string]: any;
  };
}

interface SettingsContextType {
  settings: Settings;
  loading: boolean;
  error: Error | null;
  hasUnsavedChanges: boolean;
  fetchSettings: () => Promise<void>;
  updateSetting: (category: string, key: string, value: any) => void;
  saveSettings: (category?: string | null) => Promise<void>;
  testEmailSettings: (recipientEmail: string, subject: string, body: string) => Promise<boolean>;
  getSystemHealth: () => Promise<any>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: React.ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialSettings, setInitialSettings] = useState<Settings>({});
  const {user }  = useAuth();

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getSettings();
      // Ensure response.data is an object, default to empty object if undefined or null
      const fetchedSettings = response || {};
      setSettings(fetchedSettings);
      setInitialSettings(fetchedSettings); // Store initial settings for comparison
    } catch (err: any) {
      setError(err);
      toast.error('Failed to fetch settings.');
      console.error('Error fetching settings:', err);
      // Ensure settings and initialSettings are objects even on error
      setSettings({});
      setInitialSettings({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // check if user logged in before fetching settings,
    // otherwise the system will go into infinite loop of fetching
    if(user) {
      fetchSettings();
    }
  }, [fetchSettings, user]);

  // Check for unsaved changes
  useEffect(() => {
    const changes = JSON.stringify(settings) !== JSON.stringify(initialSettings);
    setHasUnsavedChanges(changes);
  }, [settings, initialSettings]);

  const updateSetting = useCallback((category: string, key: string, value: any) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [category]: {
        ...(prevSettings[category] || {}),
        [key]: value,
      },
    }));
  }, []);

  const saveSettings = useCallback(async (category: string | null = null) => {
    setLoading(true);
    setError(null);
    try {
      if (category) {
        // Save specific category
        await api.updateSettings(category, settings[category]);
        toast.success(`${category} settings saved successfully!`);
      } else {
        // Save all categories (if no specific category is provided)
        for (const cat in settings) {
          await api.updateSettings(cat, settings[cat]);
        }
        toast.success('All settings saved successfully!');
      }
      setInitialSettings(settings); // Update initial settings after successful save
      setHasUnsavedChanges(false);
    } catch (err: any) {
      setError(err);
      toast.error('Failed to save settings.');
      console.error('Error saving settings:', err);
    } finally {
      setLoading(false);
    }
  }, [settings]);

  const testEmailSettings = useCallback(async (recipientEmail: string, subject: string, body: string) => {
    try {
      const response = await api.testEmailSettings({ recipientEmail, subject, body });
      toast.success(response.message || 'Test email sent successfully!');
      return true;
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send test email.');
      console.error('Error sending test email:', err);
      return false;
    }
  }, []);

  const getSystemHealth = useCallback(async () => {
    try {
      const response = await api.getSystemHealth();
      return response.checks;
    } catch (err) {
      toast.error('Failed to fetch system health.');
      console.error('Error fetching system health:', err);
      return null;
    }
  }, []);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        hasUnsavedChanges,
        fetchSettings,
        updateSetting,
        saveSettings,
        testEmailSettings,
        getSystemHealth,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
